/**
 * A MEMÓRIA DA ÁREA "Sistema": o que a pessoa escolheu e digitou aqui sobrevive à ida a outra área,
 * como no admin atual.
 *
 * POR QUE ELA EXISTE: no legado as 11 sub-abas são `div` fixos da mesma página. Trocar de aba
 * principal só troca duas classes e chama os carregadores (`admin.html:6440-6456`): a sub-aba
 * escolhida, os campos digitados, o arquivo escolhido e os parágrafos de status continuam no DOM.
 * No admin novo cada área é um componente por rota (`src/rotas.tsx`), que DESMONTA ao sair, e o
 * estado do React morre junto (divergência D1 da verificação de 19/09/2026). Guardar o estado aqui,
 * fora do componente, faz a volta ao Sistema encontrar o que ficou, como no legado.
 *
 * O QUE ENTRA E O QUE NÃO ENTRA, medido no legado:
 *   - Entram a sub-aba ativa, os formulários fixos (Usuários, Vínculo, E-mail, Manual Foto, Pessoas,
 *     Gift Cards, Cupom, Cliente Comunidade) e os 8 status que moram fora da lista
 *     (`admin.html:1981, 2040, 2071, 2093, 2112, 2147, 2178, 2192`).
 *   - NÃO entram as listas: a volta relê as 10 sub-abas (`admin.html:6456`), e cada sub-aba já lê
 *     ao montar. Nem Visibilidade, Preço e Roteiro, cujos campos e status moram DENTRO da lista e o
 *     legado redesenha do banco na volta (`admin.html:8976`, `9356`, `9498`).
 *
 * VIVE ENQUANTO A PÁGINA VIVE, igual ao legado: recarregar a página zera tudo, e o Sair NÃO zera,
 * porque o Sair do legado só esconde o painel e mostra o login, sem recarregar (`admin.html:3357-3359`).
 * Isso inclui a senha provisória digitada em Usuários: é o legado, reproduzido; tirar é decisão do
 * fundador (item da lista de melhorias em `progresso/PARIDADE-A6.md`).
 *
 * UM GESTO QUE TERMINA DEPOIS DE A TELA SAIR também escreve aqui (o legado escreve no DOM escondido),
 * e por isso a leitura é por assinatura (`useSyncExternalStore`): quem estiver desenhando a chave
 * redesenha quando ela muda, esteja o gesto nascido nesta montagem ou na anterior.
 */

// A assinatura de um valor de fora do React, e a memória de função.
import { useCallback, useSyncExternalStore } from 'react';
// "Hoje" em UTC, a mesma conta do legado.
import { hojeUTC } from '../financeiro/apoio.js';

// Os valores guardados, por chave. Um `Map` de módulo: nasce com a página e morre com ela.
const valores = new Map<string, unknown>();
// Quem desenha cada chave, para redesenhar quando ela muda.
const ouvintes = new Map<string, Set<() => void>>();

/**
 * O mínimo do seletor de validade do Gift Card e do Cupom, calculado UMA vez, na carga da página,
 * como o bloco do legado (`admin.html:7234-7238`), e não a cada desenho (divergência D6). Com a
 * página aberta depois da meia-noite UTC (21:00 em Brasília), o legado continua oferecendo o dia da
 * carga no seletor e recusa no clique ("A validade não pode ser anterior a hoje."), e aqui também.
 */
export const MINIMO_DA_VALIDADE = hojeUTC();

/** Lê uma chave; na primeira leitura, guarda o valor inicial (é o que a tela desenha ao nascer). */
export function lerDaMemoria<T>(chave: string, inicial: T): T {
  if (!valores.has(chave)) valores.set(chave, inicial);
  return valores.get(chave) as T;
}

/** Grava uma chave e avisa quem a desenha. Gravar o mesmo valor não redesenha ninguém. */
export function gravarNaMemoria<T>(chave: string, valor: T): void {
  if (valores.has(chave) && Object.is(valores.get(chave), valor)) return;
  valores.set(chave, valor);
  ouvintes.get(chave)?.forEach((aoMudar) => aoMudar());
}

/**
 * O `useState` da área, com o valor guardado na memória da página. Mesma forma de uso: devolve o
 * valor e a função que troca (aceita o valor novo ou uma função do anterior).
 */
export function useMemoria<T>(chave: string, inicial: T): readonly [T, (novo: T | ((anterior: T) => T)) => void] {
  // Entra e sai da lista de quem desenha a chave, junto com o componente.
  const assinar = useCallback(
    (aoMudar: () => void) => {
      const conjunto = ouvintes.get(chave) ?? new Set<() => void>();
      ouvintes.set(chave, conjunto);
      conjunto.add(aoMudar);
      return () => {
        conjunto.delete(aoMudar);
      };
    },
    [chave],
  );
  // O valor guardado; o inicial só vale na primeira leitura da página.
  const valor = useSyncExternalStore(assinar, () => lerDaMemoria(chave, inicial));
  // A troca lê o anterior da memória, e não do desenho: um gesto antigo pode ter mudado o valor.
  const trocar = useCallback(
    (novo: T | ((anterior: T) => T)) => {
      const anterior = valores.get(chave) as T;
      gravarNaMemoria(chave, typeof novo === 'function' ? (novo as (anterior: T) => T)(anterior) : novo);
    },
    [chave],
  );
  return [valor, trocar] as const;
}

/**
 * O status das sub-abas cujo parágrafo mora FORA da lista no legado: a mesma regra do
 * `useStatusTemporario` de `comum/` (mostrar e apagar depois do prazo, só se ainda for a mesma
 * mensagem, como o `if (el.textContent === msg)` do legado; ou fixar, sem prazo), mas com o texto
 * guardado na memória da página, para sobreviver à ida a outra área. O relógio também vive fora do
 * componente, como o `setTimeout` do legado, que corre com a aba escondida.
 */
export function useStatusDaArea(
  chave: string,
  // 3000 ms: o prazo da maioria dos status do legado; "Acesso criado!" usa 5000.
  prazoMs = 3000,
): readonly [string, (mensagem: string) => void, (mensagem: string) => void] {
  const [texto] = useMemoria(chave, '');
  // Mostra e agenda a limpeza.
  const mostrar = useCallback(
    (mensagem: string) => {
      gravarNaMemoria(chave, mensagem);
      window.setTimeout(() => {
        // Só apaga se ainda for a mesma mensagem: uma nova no meio do caminho fica.
        if (valores.get(chave) === mensagem) gravarNaMemoria(chave, '');
      }, prazoMs);
    },
    [chave, prazoMs],
  );
  // Mostra sem prazo: o texto fica até a próxima mensagem.
  const fixar = useCallback((mensagem: string) => gravarNaMemoria(chave, mensagem), [chave]);
  return [texto, mostrar, fixar] as const;
}
